import timezones from "@/assets/timezones.json";
import type { SessionContext } from "@/features/sessions";
import { countryName, regionFlag } from "@/features/utils/display-names";
import { useTranslation } from "@/features/utils/i18n";
import {
  DesktopIcon,
  DeviceMobileIcon,
  GlobeIcon,
  LinkIcon,
  MapPinIcon,
  TranslateIcon,
} from "@phosphor-icons/react";
import { MetaIcon } from "./conversation-meta";

/**
 * Who was behind a web-widget conversation: address, country, the languages their
 * browser asks for, the browser itself and the site it was opened from.
 *
 * A call carries a number; a web visitor used to carry nothing, and the conversation
 * header read "Contact inconnu" with no way to tell a visitor from Lyon from a bot. The
 * backend reads all of it off the request that created the conversation
 * (`sessions/services/session/web-visitor.ts`) — every value is what the visitor's
 * browser said, so it is shown as information, never as proof.
 *
 * The country comes from the CDN in front of us when there is one; without it, the
 * browser's timezone names it well enough ("Europe/Paris" is France), and the tooltip
 * says which of the two it is.
 */
export function VisitorMeta({ context }: { context?: SessionContext }) {
  const { t, i18n } = useTranslation("calls");
  if (!context || context.channel !== "web_widget") return null;

  const fromTimezone =
    !context.country_code && context.timezone
      ? (timezones as Record<string, string>)[context.timezone]
      : undefined;
  const countryCode = context.country_code || fromTimezone;
  const device = context.user_agent
    ? describeUserAgent(context.user_agent)
    : undefined;
  const languages = context.browser_languages || [];

  if (!context.ip && !countryCode && !device && !languages.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <span className="text-xs font-medium text-muted-foreground">
        {t("visitor.title")}
      </span>
      {context.ip && (
        <MetaIcon icon={GlobeIcon} label={t("visitor.ip")} value={context.ip} />
      )}
      {countryCode && (
        <MetaIcon
          icon={MapPinIcon}
          label={
            fromTimezone
              ? t("visitor.countryFromTimezone", {
                  timezone: context.timezone,
                })
              : t("visitor.country")
          }
          value={`${regionFlag(countryCode)} ${countryName(countryCode, i18n.language)}`}
        />
      )}
      {languages.length > 0 && (
        <MetaIcon
          icon={TranslateIcon}
          label={t("visitor.languages")}
          value={languages.join(", ")}
        />
      )}
      {device && (
        <MetaIcon
          icon={device.mobile ? DeviceMobileIcon : DesktopIcon}
          label={`${t("visitor.browser")} — ${context.user_agent}`}
          value={device.label}
        />
      )}
      {context.page_origin && (
        <MetaIcon
          icon={LinkIcon}
          label={t("visitor.site")}
          value={context.page_origin.replace(/^https?:\/\//, "")}
        />
      )}
    </div>
  );
}

/**
 * Enough of a user agent to read at a glance — "Chrome · Android". The full string is
 * in the tooltip; this is not a device database, and the order of the tests matters
 * (Edge and Opera also say "Chrome", Chrome also says "Safari").
 */
export const describeUserAgent = (
  ua: string,
): { label: string; mobile: boolean } | undefined => {
  const browser = /Edg(e|A|iOS)?\//.test(ua)
    ? "Edge"
    : /OPR\/|Opera/.test(ua)
      ? "Opera"
      : /SamsungBrowser\//.test(ua)
        ? "Samsung Internet"
        : /Firefox\/|FxiOS\//.test(ua)
          ? "Firefox"
          : /Chrome\/|CriOS\//.test(ua)
            ? "Chrome"
            : /Safari\//.test(ua)
              ? "Safari"
              : undefined;
  const os = /iPhone|iPad|iPod/.test(ua)
    ? "iOS"
    : /Android/.test(ua)
      ? "Android"
      : /Windows/.test(ua)
        ? "Windows"
        : /Mac OS X|Macintosh/.test(ua)
          ? "macOS"
          : /Linux/.test(ua)
            ? "Linux"
            : undefined;
  const label = [browser, os].filter(Boolean).join(" · ");
  if (!label) return undefined;
  return { label, mobile: /Mobi|iPhone|iPod|Android/.test(ua) };
};
